import React, { useState, useRef } from 'react';

interface VoiceInputProps {
  onTranscription: (text: string, autoSend?: boolean) => void;
  disabled?: boolean;
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscription, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const autoSendRef = useRef<boolean>(false); // Use ref instead of state for immediate access

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        // Use the ref value directly, which will have the correct value
        transcribeAudio(audioBlob, autoSendRef.current);
      };

      audioChunks.current = [];
      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setError('');
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access denied');
    }
  };

  const stopRecording = (shouldAutoSend: boolean = false) => {
    if (mediaRecorder.current && isRecording) {
      // Set the ref value immediately before stopping
      autoSendRef.current = shouldAutoSend;
      mediaRecorder.current.stop();
      setIsRecording(false);
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    }
  };

  const transcribeAudio = async (audioBlob: Blob, shouldAutoSend: boolean = false) => {
    setIsTranscribing(true);
    setError('');

    console.log('VoiceInput: Starting transcription with autoSend =', shouldAutoSend);

    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-input.webm');

      const response = await fetch('/api/voice-to-text', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const result = await response.json() as { text?: string; transcript?: string };
      const transcriptionText = result.text || result.transcript || '';
      
      if (transcriptionText.trim()) {
        console.log('VoiceInput: Calling onTranscription with autoSend =', shouldAutoSend);
        onTranscription(transcriptionText.trim(), shouldAutoSend);
      } else {
        setError('No speech detected. Please try again.');
      }

    } catch (err) {
      console.error('Voice transcription error:', err);
      setError('Transcription failed. Please try again.');
    } finally {
      setIsTranscribing(false);
      // Reset the autoSend ref
      autoSendRef.current = false;
    }
  };

  const handleStartRecording = () => {
    startRecording();
  };

  const handleStopRecording = () => {
    console.log('VoiceInput: Red button clicked - should NOT auto-send');
    stopRecording(false); // Just transcribe, don't auto-send
  };

  const handleStopAndSend = () => {
    console.log('VoiceInput: Green button clicked - should auto-send');
    stopRecording(true); // Transcribe and auto-send
  };

  return (
    <div className="relative">
      {!isRecording ? (
        // Start Recording Button
        <button
          onClick={handleStartRecording}
          disabled={disabled || isTranscribing}
          className={`p-2 rounded-lg transition-all duration-200 ${
            isTranscribing
              ? 'bg-gray-300 text-gray-500'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={
            isTranscribing
              ? 'Processing speech...'
              : 'Click to start voice input'
          }
        >
          {isTranscribing ? (
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-500"></div>
          ) : (
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            </svg>
          )}
        </button>
      ) : (
        // Recording Controls - Show both buttons
        <div className="flex space-x-1">
          {/* Stop Recording Button (Red Dot) */}
          <button
            onClick={handleStopRecording}
            disabled={disabled}
            className="p-2 rounded-lg bg-red-500 text-white animate-pulse hover:bg-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Stop recording and put text in chat box"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 6h12v12H6z" />
            </svg>
          </button>

          {/* Stop and Send Button (Checkmark) */}
          <button
            onClick={handleStopAndSend}
            disabled={disabled}
            className="p-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Stop recording and automatically send to AI"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </button>
        </div>
      )}

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-red-100 text-red-700 text-xs rounded-lg border border-red-200 whitespace-nowrap">
          {error}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-200"></div>
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
      )}
    </div>
  );
};

export default VoiceInput; 